import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { X, CheckCircle2, ShieldCheck } from "lucide-react";
import { useT } from "@/i18n";

const PAYPAL_CLIENT_ID = "BAAAgBH2enVNYzolDy3uBJc2YS2tn1ddb5eH0nBp0L2qEkx3zuy8DloSDYJ2MrqZSb0KiMJidGeJDxNfZA";
const PAYPAL_HOSTED_BUTTON_ID = "QH5L9HX26LH2J";
const PAYPAL_SDK_SRC = `https://www.paypal.com/sdk/js?client-id=${PAYPAL_CLIENT_ID}&components=hosted-buttons&disable-funding=venmo&currency=EUR`;

// Load the PayPal SDK once for the whole tab and return a promise that resolves when ready.
let paypalSdkPromise = null;
function loadPaypalSdk() {
  if (typeof window === "undefined") return Promise.reject(new Error("SSR"));
  if (window.paypal && window.paypal.HostedButtons) return Promise.resolve(window.paypal);
  if (paypalSdkPromise) return paypalSdkPromise;
  paypalSdkPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${PAYPAL_SDK_SRC}"]`);
    if (existing) {
      existing.addEventListener("load", () => resolve(window.paypal));
      existing.addEventListener("error", reject);
      return;
    }
    const s = document.createElement("script");
    s.src = PAYPAL_SDK_SRC;
    s.async = true;
    s.onload = () => resolve(window.paypal);
    s.onerror = reject;
    document.head.appendChild(s);
  });
  return paypalSdkPromise;
}

export default function PricingModal({ open, onClose }) {
  const { t } = useT();
  const containerRef = useRef(null);

  // ESC to close + lock body scroll
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  // Mount the PayPal hosted button when modal opens
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const containerId = `paypal-container-${PAYPAL_HOSTED_BUTTON_ID}`;
    loadPaypalSdk()
      .then((paypal) => {
        if (cancelled || !paypal?.HostedButtons) return;
        const node = document.getElementById(containerId);
        if (!node) return;
        // Clear any previous mount before re-rendering
        node.innerHTML = "";
        paypal
          .HostedButtons({ hostedButtonId: PAYPAL_HOSTED_BUTTON_ID })
          .render(`#${containerId}`)
          // eslint-disable-next-line no-console
          .catch((err) => console.error("PayPal render failed", err));
      })
      // eslint-disable-next-line no-console
      .catch((err) => console.error("PayPal SDK failed to load", err));
    return () => { cancelled = true; };
  }, [open]);

  if (!open) return null;

  return (
    <div
      data-testid="pricing-modal"
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-[100] flex items-center justify-center px-4 py-6 md:py-10"
    >
      {/* backdrop */}
      <div
        className="absolute inset-0 bg-black/80 backdrop-blur-md"
        onClick={onClose}
        data-testid="pricing-modal-backdrop"
      />

      {/* dialog */}
      <div className="relative z-10 w-full max-w-2xl max-h-[92vh] overflow-y-auto bg-[#0c0c0c] border border-[#D4AF37]/30 shadow-2xl">
        {/* close */}
        <button
          ref={containerRef}
          onClick={onClose}
          data-testid="pricing-modal-close"
          aria-label={t("modal.close")}
          className="absolute top-4 right-4 h-9 w-9 inline-flex items-center justify-center border border-white/15 text-[#A1A1AA] hover:text-[#D4AF37] hover:border-[#D4AF37] transition-colors"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="p-7 md:p-10">
          <p className="text-[10px] uppercase tracking-[0.4em] text-[#D4AF37]">{t("pricing.eyebrow")}</p>
          <h3 className="font-serif text-2xl md:text-3xl font-light mt-2 leading-tight">{t("modal.title")}</h3>

          {/* Summary */}
          <div className="mt-7 border border-white/10 bg-black/40">
            <div className="px-5 py-3 border-b border-white/10 text-[10px] uppercase tracking-[0.32em] text-[#A1A1AA]">
              {t("modal.summary.label")}
            </div>
            <div className="px-5 py-5 flex items-baseline justify-between gap-4">
              <div>
                <div className="font-serif text-lg">{t("modal.summary.item")}</div>
                <div className="text-xs text-[#A1A1AA] mt-1">{t("pricing.plan.tag")}</div>
              </div>
              <div className="font-serif text-3xl text-[#D4AF37] tabular-nums whitespace-nowrap">
                {t("pricing.plan.price")} {t("pricing.plan.currency")}
              </div>
            </div>
            <ul className="px-5 pb-5 space-y-2 text-sm text-[#C4C4C7]">
              {["pricing.feature.1","pricing.feature.2","pricing.feature.3","pricing.feature.4","pricing.feature.5"].map((k) => (
                <li key={k} className="flex items-start gap-3">
                  <CheckCircle2 className="h-4 w-4 text-[#D4AF37] shrink-0 mt-0.5" />
                  <span>{t(k)}</span>
                </li>
              ))}
            </ul>
            <div className="px-5 py-3 border-t border-white/10 flex items-baseline justify-between text-sm">
              <span className="text-[#A1A1AA] uppercase tracking-[0.28em] text-[10px]">{t("modal.summary.total")}</span>
              <span className="font-serif text-xl text-white tabular-nums">{t("pricing.plan.price")} {t("pricing.plan.currency")}</span>
            </div>
          </div>

          {/* PayPal button mount point */}
          <div className="mt-7">
            <div
              id={`paypal-container-${PAYPAL_HOSTED_BUTTON_ID}`}
              data-testid="paypal-button-container"
              className="min-h-[55px]"
            />
            <p className="mt-3 text-[10px] uppercase tracking-[0.28em] text-[#52525B] text-center">
              {t("pricing.note")}
            </p>
          </div>

          {/* Guarantee */}
          <div className="mt-7 border-l-2 border-[#D4AF37] bg-[#141414] p-4">
            <div className="flex items-start gap-3">
              <ShieldCheck className="h-4 w-4 text-[#D4AF37] mt-0.5 shrink-0" />
              <p className="text-xs text-[#C4C4C7] leading-relaxed">{t("modal.guarantee")}</p>
            </div>
          </div>

          {/* Has code already */}
          <div className="mt-7 pt-6 border-t border-white/10 text-center">
            <Link
              to="/login"
              data-testid="modal-already-has-code"
              className="text-xs uppercase tracking-[0.28em] text-[#A1A1AA] hover:text-[#D4AF37] transition-colors"
            >
              {t("modal.has_code")} →
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
